import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";

import { Eye, Plus, Send, Loader2, ExternalLink, Users, Megaphone, X } from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { supabase } from "@/lib/supabase";
import { SlideOver } from "@/components/ui/SlideOver";
import { InfoTip } from "@/components/InfoTip";
import { displayCustomerName } from "@/lib/format";
import { CampaignsPanel, CampaignLaunchForm } from "./CampaignsPanel";
import { AudiencesPanel, type AudiencePick } from "./AudiencesPanel";
import { TemplateGallery } from "./TemplateGallery";
import { SmsCampaignForm } from "./SmsCampaignForm";
import { useMarketingTemplates as useGalleryTemplates, type MarketingTemplate } from "./templates";
import { CustomerPicker, type PickedCustomer } from "@/components/CustomerPicker";
import { resolveAudience, useAudiences, STARTER_AUDIENCES, type AudienceCount, type AudienceSpec } from "./audiences";
import type { CampaignRecipient } from "./campaigns";

// ============================================================================
// MarketingView (EMAIL-SEND) — the marketing front door. A gallery of the
// on-brand templates with a LIVE personalized preview (render-template resolves
// each {{token}} from the mirror for a chosen customer) + a test send. The
// drag-drop builder is one click away per template. Bulk campaign send lands
// once Microsoft 365 sending is configured.
// ============================================================================

type Tpl = MarketingTemplate;

const CAMPAIGN_CAP = 5000; // per campaign (matches the All Customers path); the outbox paces the send

export function MarketingView() {
  const { data: templates } = useGalleryTemplates(false);
  const [sel, setSel] = useState<Tpl | null>(null);
  // M3: preview picks a customer by SEARCH (the same picker the Inbox uses), not a typed number.
  const [previewCust, setPreviewCust] = useState<PickedCustomer | null>({ custno: "0001321", name: "All Seasons Wild Bird Store", address: null, city: null });
  const custno = previewCust?.custno ?? "0001321";
  const [html, setHtml] = useState("");
  const [subject, setSubject] = useState("");
  const [rendering, setRendering] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sendMsg, setSendMsg] = useState<string | null>(null);
  // M2: the audience is a SPEC resolved by the database (saved audiences +
  // starters), never a hard-coded segment. The resolved list becomes a
  // CAMPAIGN (outbox + comms-run) — the browser loop is long gone.
  const { data: savedAudiences } = useAudiences();
  const [pick, setPick] = useState<AudiencePick | null>(null);
  const [audience, setAudience] = useState<AudienceCount | null>(null);
  const [audLoading, setAudLoading] = useState(false);
  const [recipients, setRecipients] = useState<CampaignRecipient[] | null>(null);
  // "Use in a campaign" from the Audiences panel: remember the pick, ask for a template.
  const [pendingPick, setPendingPick] = useState<AudiencePick | null>(null);
  // M6: the text campaign form (opened from Campaigns or from an audience's Text button).
  const [smsForm, setSmsForm] = useState<{ open: boolean; pick: AudiencePick | null }>({ open: false, pick: null });
  // ?new_audience=<json> — the All Customers report hands its filters here.
  const [params, setParams] = useSearchParams();
  const [openNew, setOpenNew] = useState<{ name?: string; spec: AudienceSpec } | null>(null);
  useEffect(() => {
    const raw = params.get("new_audience");
    if (!raw) return;
    try { setOpenNew(JSON.parse(raw) as { name?: string; spec: AudienceSpec }); } catch { /* ignore a bad link */ }
    const n = new URLSearchParams(params); n.delete("new_audience"); setParams(n, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  const audienceOptions: { key: string; label: string; pick: AudiencePick }[] = [
    ...(savedAudiences ?? []).map((a) => ({ key: `saved:${a.id}`, label: a.name, pick: { label: a.name, audienceId: a.id, spec: a.mode === "static" && a.static_custnos?.length ? { custnos: a.static_custnos, active_only: false } : a.spec } })),
    ...STARTER_AUDIENCES.map((s) => ({ key: `starter:${s.key}`, label: `${s.name} (starter)`, pick: { label: s.name, spec: s.spec } })),
  ];
  const pickKey = pick ? (pick.audienceId ? `saved:${pick.audienceId}` : `starter:${STARTER_AUDIENCES.find((s) => s.spec === pick.spec)?.key ?? ""}`) : "";

  async function checkAudience(p: AudiencePick | null = pick) {
    if (!p) return;
    setAudLoading(true); setAudience(null); setRecipients(null);
    try {
      const [{ data }, resolved] = await Promise.all([
        supabase.rpc("pegasus_audience_count", { p_spec: p.spec, p_channel: "email" }),
        resolveAudience(p.spec, "email"),
      ]);
      setAudience((data as AudienceCount) ?? null);
      setRecipients(resolved.recipients);
    } finally { setAudLoading(false); }
  }
  async function openPreview(t: Tpl) {
    setSel(t); setHtml(""); setSubject(""); setErr(null); setSendMsg(null);
    if (pendingPick) { setPick(pendingPick); setPendingPick(null); void checkAudience(pendingPick); }
    await render(t.key, custno);
  }
  async function render(key: string, who: string) {
    setRendering(true); setErr(null);
    const { data, error } = await supabase.functions.invoke("render-template", { body: { template_key: key, custno: who } });
    setRendering(false);
    const r = data as { ok?: boolean; html?: string; subject?: string; error?: string } | null;
    if (error || !r?.ok) { setErr(r?.error || error?.message || "Couldn't render this template for that customer."); setHtml(""); return; }
    setHtml(r.html ?? ""); setSubject(r.subject ?? "");
  }
  async function sendTest() {
    setSendMsg("Sending…");
    const { data: u } = await supabase.auth.getUser();
    const to = u.user?.email;
    if (!to) { setSendMsg("Couldn't find your email address."); return; }
    const { data, error } = await supabase.functions.invoke("send-email", {
      body: { to: [to], subject, html, custno, message_type: "marketing-test" },
    });
    const r = data as { ok?: boolean; error?: string } | null;
    if (error || !r?.ok) {
      setSendMsg(r?.error === "secrets_missing"
        ? "Email sending isn't switched on yet — add the Microsoft 365 keys to go live."
        : `Couldn't send: ${r?.error || error?.message}`);
    } else setSendMsg(`Sent to ${to} — check your inbox.`);
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: "easeOut" }} className="mx-auto max-w-7xl p-8">
      <PageHeader
        icon={Megaphone}
        title="Marketing"
        description="Your on-brand email templates. Preview one personalized for any customer, tweak it in the builder, then send. Every email pulls the customer’s name, address and system from their record."
        actions={
          <Link to="/email-builder?from=marketing" className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-lg bg-brand-700 px-4 text-sm font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800">
            <Plus className="h-4 w-4" /> New email
          </Link>
        }
      />

      <CampaignsPanel templateNames={Object.fromEntries((templates ?? []).map((t) => [t.key, t.name]))} onNewText={() => setSmsForm({ open: true, pick: null })} />

      <AudiencesPanel
        onUse={(p) => { setPendingPick(p); setPick(p); setAudience(null); setRecipients(null); document.getElementById("mkt-templates")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
        onText={(p) => setSmsForm({ open: true, pick: p })}
        openNew={openNew}
        onOpenedNew={() => setOpenNew(null)}
      />

      {pendingPick && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-brand-200 bg-brand-50/60 px-3 py-2 text-sm text-slate-700">
          <Users className="h-4 w-4 text-brand-600" />
          <span>Audience picked: <strong>{pendingPick.label}</strong> — now choose the email below and hit Preview to send it as a campaign.</span>
          <button type="button" onClick={() => setPendingPick(null)} className="ml-auto rounded p-1 text-slate-400 hover:bg-white hover:text-slate-700" aria-label="Clear"><X className="h-4 w-4" /></button>
        </div>
      )}

      <TemplateGallery onPreview={(t) => void openPreview(t)} />

      {smsForm.open && <SmsCampaignForm open onClose={() => setSmsForm({ open: false, pick: null })} initialPick={smsForm.pick} />}

      <SlideOver
        open={!!sel}
        onClose={() => setSel(null)}
        size="lg"
        title={sel?.name}
        subtitle={subject || sel?.subject || undefined}
        footer={sel ? (
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-slate-400">{sendMsg ?? "Test sends to your own inbox — customers aren’t emailed."}</span>
            <div className="flex items-center gap-2">
              <Link to={`/email-builder?template=${sel.key}&from=marketing`} className="inline-flex items-center gap-1.5 rounded-lg border border-hairline px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <ExternalLink className="h-4 w-4" /> Open in builder
              </Link>
              <button onClick={sendTest} disabled={!html} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
                <Send className="h-4 w-4" /> Send test to me
              </button>
            </div>
          </div>
        ) : undefined}
      >
        {sel && (
          <div>
            <div className="mb-3 flex items-end gap-2">
              <div className="min-w-0 flex-1">
                <span className="mb-1 block text-[11px] font-medium text-slate-500">Preview as</span>
                <CustomerPicker selected={previewCust} onSelect={(c) => { setPreviewCust(c); void render(sel.key, c.custno); }} onClear={() => setPreviewCust(null)} placeholder="Search a customer to preview their personalized email…" />
              </div>
              <button onClick={() => render(sel.key, custno)} disabled={rendering}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-hairline px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                {rendering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Refresh
              </button>
            </div>
            {err ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">{err}</div>
            ) : rendering && !html ? (
              <div className="py-16 text-center text-sm text-slate-400">Rendering personalized preview…</div>
            ) : (
              <iframe srcDoc={html} title="Email preview" sandbox="allow-same-origin" className="h-[640px] w-full rounded-lg border border-hairline bg-white" />
            )}

            {/* Campaign send — to a whole segment, each personalized */}
            <div className="mt-5 rounded-xl border border-hairline bg-slate-50/50 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Users className="h-4 w-4 text-brand-600" /> Send as a campaign
                <InfoTip label="Where campaigns and audiences live">Once scheduled, the campaign shows in Campaigns, where you can cancel it any time. Build or edit audiences in the Audiences section.</InfoTip>
              </div>
              <div className="mt-3 flex items-end gap-2">
                <label className="flex-1">
                  <span className="mb-1 block text-[11px] font-medium text-slate-500">Audience</span>
                  <select value={pickKey} onChange={(e) => { const o = audienceOptions.find((x) => x.key === e.target.value); setPick(o?.pick ?? null); setAudience(null); setRecipients(null); }} className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700">
                    <option value="">Pick an audience…</option>
                    {(savedAudiences ?? []).length > 0 && (
                      <optgroup label="Saved audiences">
                        {audienceOptions.filter((o) => o.key.startsWith("saved:")).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                      </optgroup>
                    )}
                    <optgroup label="Starters">
                      {audienceOptions.filter((o) => o.key.startsWith("starter:")).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </optgroup>
                  </select>
                </label>
                <button onClick={() => void checkAudience()} disabled={audLoading || !pick} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  {audLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />} Check audience
                </button>
              </div>
              {audience && recipients && pick && (
                <div className="mt-3 space-y-2">
                  <div className="rounded-lg border border-hairline bg-white p-3 text-sm text-slate-700">
                    <strong className="text-brand-700">{audience.sendable.toLocaleString()}</strong> customers we can email
                    <span className="ml-1 text-[11px] text-slate-400">
                      of {audience.matched.toLocaleString()} matching{audience.no_endpoint ? ` · ${audience.no_endpoint.toLocaleString()} no email` : ""}{audience.unsubscribed ? ` · ${audience.unsubscribed.toLocaleString()} unsubscribed` : ""}
                      {audience.sample.length > 0 && <> — e.g. {audience.sample.map((n) => displayCustomerName(null, n)).join(", ")}…</>}
                    </span>
                  </div>
                  <CampaignLaunchForm
                    defaultName={`${sel.name} — ${pick.label} — ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
                    templateKey={sel.key}
                    templateName={sel.name}
                    audience={{ kind: "audience", audience_id: pick.audienceId ?? null, spec: pick.spec, label: pick.label }}
                    audienceLabel={pick.label}
                    recipients={recipients.slice(0, CAMPAIGN_CAP)}
                    excludedNote={recipients.length > CAMPAIGN_CAP ? `Capped at ${CAMPAIGN_CAP.toLocaleString()} for now; larger lists split into more campaigns.` : null}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </SlideOver>
    </motion.div>
  );
}
